import { useCallback, useEffect, useState } from "react";
import "cropperjs/dist/cropper.css";
import {
  PostUserDto,
  usePostProfilePicture,
  usePostUserQuery,
  useUserDataQuery,
} from "../../api/userApi.ts";
import { useNavigate, Link } from "react-router";
import { useAuth } from "react-oidc-context";
import { useSupportedLanguages } from "../../api/languageApi.ts";
import { ProfilePicture } from "../../components/profile/ProfilePicture.tsx";
import {
  getTranslation,
  useSupportedInstruments,
} from "../../api/instrumentApi.ts";
import Container from "../../components/Container.tsx";
import ContainerHeader from "../../components/ContainerHeader.tsx";
import FormField from "../../components/FormField.tsx";
import TextInputField from "../../components/TextInputField.tsx";
import ActionButton from "../../components/ActionButton.tsx";
import { NeededTeacherActions } from "../../components/profile/NeededTeacherActions.tsx";
import { Pencil, SquarePen, UserCircle } from "lucide-react";
import { ImageUploadWithCrop } from "../../components/media/ImageUploadWithCrop.tsx";
import TextAreaInputField from "../../components/form/TextAreaInputField.tsx";
import { Trans, useLingui } from "@lingui/react/macro";
import TileSelector, { TileOption } from "../../components/TileSelector.tsx";
import { useSupportedGenres } from "../../api/genreApi.ts";
import { useLanguageStore } from "../../stores/languageStore.ts";
import { useBecomeTeacherStore } from "../../stores/becomeTeacherStore.ts";

type EditableUserData = PostUserDto & { notificationLanguage?: string };

const NOTIFICATION_LANGUAGE_OPTIONS = [
  { value: "en", label: "English 🇬🇧" },
  { value: "de", label: "Deutsch 🇩🇪" },
];

export const EditProfile = () => {
  const { user } = useAuth();
  const { t } = useLingui();
  const { data, refetch } = useUserDataQuery(user?.profile.sub);
  const { mutate: uploadImage, isPending: isUploadImagePending } =
    usePostProfilePicture();
  const { mutate: postUserDto } = usePostUserQuery();
  const { isBecomingTeacher, setIsBecomingTeacher } = useBecomeTeacherStore();

  const [modifiableUserData, setModifiableUserData] =
    useState<EditableUserData>({
      firstName: "",
      lastName: "",
      email: "",
      languages: [],
      instruments: [],
      genres: [],
      introduction: "",
      setupWizardDone: false,
      notificationLanguage: "de",
    });
  const navigate = useNavigate();
  const [languageOptions, setLanguageOptions] = useState<TileOption[]>([]);
  const [instrumentOptions, setInstrumentOptions] = useState<TileOption[]>([]);
  const [genreOptions, setGenreOptions] = useState<TileOption[]>([]);
  const { data: supportedLanguages } = useSupportedLanguages();
  const { data: supportedInstruments } = useSupportedInstruments();
  const { data: supportedGenres } = useSupportedGenres();
  const [isEditingProfilePicture, setIsEditingProfilePicture] = useState(false);
  const [imageBlob, setImageBlob] = useState<Blob>();
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>();
  const { language } = useLanguageStore();

  const [editingLanguages, setEditingLanguages] = useState(false);
  const [editingInstruments, setEditingInstruments] = useState(false);
  const [editingGenres, setEditingGenres] = useState(false);
  const [editingNotificationLanguage, setEditingNotificationLanguage] =
    useState(false);

  useEffect(() => {
    setLanguageOptions(
      supportedLanguages?.map((language) => ({
        value: language.locale,
        label: language.displayText,
      })) ?? [],
    );
  }, [supportedLanguages]);

  useEffect(() => {
    setInstrumentOptions(
      supportedInstruments.map((instrument) => ({
        value: instrument.name,
        label: getTranslation(instrument, language),
      })),
    );
  }, [language, supportedInstruments]);

  useEffect(() => {
    setGenreOptions(
      supportedGenres?.map((genre) => ({
        value: genre.name,
        label: getTranslation(genre, language),
      })) ?? [],
    );
  }, [language, supportedGenres]);

  useEffect(() => {
    if (data) {
      setModifiableUserData({
        ...data,
        notificationLanguage:
          (data as EditableUserData).notificationLanguage ?? "en",
      });
    }
  }, [data]);

  const onLanguageChange = useCallback(
    (selections: string[]) =>
      setModifiableUserData((prev) => ({ ...prev, languages: selections })),
    [],
  );

  const onInstrumentChange = useCallback(
    (selections: string[]) =>
      setModifiableUserData((prev) => ({ ...prev, instruments: selections })),
    [],
  );

  const onGenreChange = useCallback(
    (selections: string[]) =>
      setModifiableUserData((prev) => ({ ...prev, genres: selections })),
    [],
  );

  const handleImageChange = (blob: Blob | undefined) => {
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    setImageBlob(blob);
    if (blob) {
      setImagePreviewUrl(URL.createObjectURL(blob));
    } else {
      setImagePreviewUrl(undefined);
    }
  };

  const handleImageUpload = () => {
    uploadImage(imageBlob!, {
      onSuccess: async () => {
        await refetch();
        setIsEditingProfilePicture(false);
        if (imagePreviewUrl) {
          URL.revokeObjectURL(imagePreviewUrl);
          setImagePreviewUrl(undefined);
        }
        setImageBlob(undefined);
      },
      onError: () => {
        console.error("Error while uploading profile picture");
      },
    });
  };

  const handleCancelEdit = () => {
    setIsEditingProfilePicture(false);
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl(undefined);
    }
    setImageBlob(undefined);
  };

  const postUser = () => {
    postUserDto(modifiableUserData as PostUserDto, {
      onSuccess: () => {
        navigate(`/profile/${user?.profile.sub}`);
      },
    });
  };

  const renderChips = (
    values: string[],
    options: TileOption[],
    onEdit: () => void,
    emptyLabel: string,
  ) => (
    <div className="flex flex-wrap items-center gap-2 py-1 font-medium">
      {values.length === 0 ? (
        <span className="text-sm text-gray-400 italic">{emptyLabel}</span>
      ) : (
        values.map((val) => {
          const label = options.find((o) => o.value === val)?.label ?? val;
          return (
            <div
              key={val}
              className="border-soluud-primary rounded-full border px-4"
            >
              {label}
            </div>
          );
        })
      )}
      <SquarePen
        className="h-4 w-4 cursor-pointer text-gray-500 hover:text-gray-700"
        onClick={onEdit}
      />
    </div>
  );

  const getProfilePictureEditor = () => (
    <>
      {!isEditingProfilePicture && (
        <div
          className={`relative h-32 w-32 ${isUploadImagePending ? "opacity-50" : ""}`}
        >
          <ProfilePicture
            profilePictureUrl={data?.profilePictureUrl}
            firstName={data?.firstName}
            lastName={data?.lastName}
            className="h-full w-full"
            iconSize={72}
          />
          {!isUploadImagePending && (
            <button
              type="button"
              onClick={() => setIsEditingProfilePicture(true)}
              className="hover:bg-soluud-primary-hover bg-soluud-background-card focus:ring-soluud-primary absolute right-2 bottom-2 flex cursor-pointer items-center justify-center rounded-full p-2 shadow-md focus:ring-2 focus:ring-offset-2 focus:outline-none"
              aria-label="Edit profile picture"
            >
              <Pencil size={20} className="text-gray-700" aria-hidden="true" />
            </button>
          )}
        </div>
      )}

      {isEditingProfilePicture && (
        <>
          <ImageUploadWithCrop
            showCropper={true}
            maxSizeBytes={200000}
            image={imagePreviewUrl}
            showClearImageButton={false}
            onImageChange={handleImageChange}
            isUploading={isUploadImagePending}
            renderPreview={(imageUrl, isUploading) => (
              <div className="relative h-32 w-32">
                <ProfilePicture
                  profilePictureUrl={imageUrl || data?.profilePictureUrl}
                  firstName={data?.firstName}
                  lastName={data?.lastName}
                  className="h-full w-full"
                  iconSize={72}
                />
                {isUploading && (
                  <div
                    className="absolute inset-0 flex items-center justify-center"
                    role="status"
                    aria-live="polite"
                    aria-label="Uploading profile picture"
                  >
                    <div
                      className="h-10 w-10 animate-spin rounded-full border-4 border-white border-t-transparent"
                      aria-hidden="true"
                    />
                  </div>
                )}
              </div>
            )}
          />
          <button
            onClick={handleCancelEdit}
            className="bg-soluud-primary hover:bg-soluud-primary-hover mt-2 w-full rounded-xl py-2 text-white transition hover:cursor-pointer"
          >
            Cancel
          </button>
          {imageBlob && (
            <button
              className="bg-soluud-primary hover:bg-soluud-primary-hover mt-2 w-full rounded-xl py-2 text-white transition hover:cursor-pointer"
              onClick={handleImageUpload}
            >
              Save
            </button>
          )}
        </>
      )}
    </>
  );

  return (
    modifiableUserData && (
      <Container className={"mx-auto flex w-full max-w-3xl flex-col"}>
        <ContainerHeader>
          <Trans>Your Profile</Trans>
        </ContainerHeader>
        {data?.teacherStatus && data?.teacherStatus !== "COMPLETE" && (
          <NeededTeacherActions />
        )}
        <div className={"flex flex-col items-center"}>
          {getProfilePictureEditor()}
        </div>
        <div className={"mt-4 mb-4 flex w-full flex-col items-start gap-4"}>
          <div className="flex w-full flex-col gap-4 md:flex-row">
            <TextInputField
              id="firstName"
              label={t`First Name`}
              value={modifiableUserData?.firstName}
              onChange={(e) =>
                setModifiableUserData({
                  ...modifiableUserData,
                  firstName: e.target.value,
                } as EditableUserData)
              }
              className="w-full"
            />
            <TextInputField
              id="lastName"
              label={t`Last Name`}
              value={modifiableUserData?.lastName}
              onChange={(e) =>
                setModifiableUserData({
                  ...modifiableUserData,
                  lastName: e.target.value,
                } as EditableUserData)
              }
            />
          </div>
          <TextInputField
            id="email"
            label={t`Email`}
            type="email"
            value={modifiableUserData?.email}
            onChange={(e) =>
              setModifiableUserData({
                ...modifiableUserData,
                email: e.target.value,
              } as EditableUserData)
            }
          />
          <FormField
            htmlFor="notificationLanguage"
            label={t`Notification Language`}
          >
            {editingNotificationLanguage ? (
              <div className="flex gap-2 py-1">
                {NOTIFICATION_LANGUAGE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      setModifiableUserData((prev) => ({
                        ...prev,
                        notificationLanguage: opt.value,
                      }));
                      setEditingNotificationLanguage(false);
                    }}
                    className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                      modifiableUserData.notificationLanguage === opt.value
                        ? "border-soluud-primary bg-soluud-info"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex items-center py-1 font-medium">
                <div className="border-soluud-primary rounded-full border px-4">
                  {NOTIFICATION_LANGUAGE_OPTIONS.find(
                    (o) => o.value === modifiableUserData.notificationLanguage,
                  )?.label ?? modifiableUserData.notificationLanguage}
                </div>
                <SquarePen
                  className="ml-2 h-4 w-4 cursor-pointer text-gray-500 hover:text-gray-700"
                  onClick={() => setEditingNotificationLanguage(true)}
                />
              </div>
            )}
          </FormField>
          <TextAreaInputField
            id="introduction"
            label={t`Introduction`}
            placeholder={t`Tell us a little bit about yourself.`}
            value={modifiableUserData?.introduction || ""}
            onChange={(e) =>
              setModifiableUserData({
                ...modifiableUserData,
                introduction: e.target.value,
              } as EditableUserData)
            }
            maxLength={1000}
          />
          <FormField htmlFor="languages" label={t`Languages`}>
            {editingLanguages ? (
              <div>
                <TileSelector
                  options={languageOptions}
                  selectedValues={modifiableUserData.languages || []}
                  onChange={onLanguageChange}
                />
                <ActionButton
                  variant="secondary"
                  onClick={() => setEditingLanguages(false)}
                >
                  <Trans>Done</Trans>
                </ActionButton>
              </div>
            ) : (
              renderChips(
                modifiableUserData.languages || [],
                languageOptions,
                () => setEditingLanguages(true),
                t`None selected`,
              )
            )}
          </FormField>
          <FormField htmlFor="instruments" label={t`Instruments`}>
            {editingInstruments ? (
              <div>
                <TileSelector
                  options={instrumentOptions}
                  selectedValues={modifiableUserData.instruments || []}
                  onChange={onInstrumentChange}
                />
                <ActionButton
                  variant="secondary"
                  onClick={() => setEditingInstruments(false)}
                >
                  <Trans>Done</Trans>
                </ActionButton>
              </div>
            ) : (
              renderChips(
                modifiableUserData.instruments || [],
                instrumentOptions,
                () => setEditingInstruments(true),
                t`None selected`,
              )
            )}
          </FormField>
          <FormField htmlFor="genres" label={t`Genres`}>
            {editingGenres ? (
              <div>
                <TileSelector
                  options={genreOptions}
                  selectedValues={modifiableUserData.genres || []}
                  onChange={onGenreChange}
                />
                <ActionButton
                  variant="secondary"
                  onClick={() => setEditingGenres(false)}
                >
                  <Trans>Done</Trans>
                </ActionButton>
              </div>
            ) : (
              renderChips(
                modifiableUserData.genres || [],
                genreOptions,
                () => setEditingGenres(true),
                t`None selected`,
              )
            )}
          </FormField>
        </div>
        <div className={"flex gap-4"}>
          <ActionButton variant={"primary"} onClick={() => postUser()}>
            <Trans>Save</Trans>
          </ActionButton>
          <Link to={`/profile/${user?.profile.sub}`}>
            <ActionButton variant={"secondary"}>
              <div className="flex items-center gap-2">
                <UserCircle className="h-4 w-4" />
                <Trans>View Public Profile</Trans>
              </div>
            </ActionButton>
          </Link>
          {!data?.teacherStatus && (
            <ActionButton
              variant={"secondary"}
              isLoading={isBecomingTeacher}
              onClick={() => setIsBecomingTeacher(true)}
            >
              <Trans>Become Teacher!</Trans>
            </ActionButton>
          )}
        </div>
      </Container>
    )
  );
};
